import { SliceCaseReducers, createSlice } from '@reduxjs/toolkit';

export interface IGlobalStateHomeDrawer {
  show: boolean;
  model: 'people' | 'passions' | 'skills' | 'companies' | 'clients' | 'industries' | 'disciplines';
}

export interface IGlobalStateResourceModal {
  show: boolean;
  slug: string | null;
  unique_id?: string | null;
  isEditing?: boolean;
  originScrollPosition?: number;
}

export interface IGlobalStateArticleModal {
  show: boolean;
  slug: string | null;
  unique_id?: string | null;
  isEditing?: boolean;
}

export interface GlobalState {
  homeDrawer: IGlobalStateHomeDrawer;
  resourceModal: IGlobalStateResourceModal;
  articleModal: IGlobalStateArticleModal;
  tiptapEditorFocus: boolean;
  dialogOpen: boolean;
  windowDimensions: {
    width: number;
    height: number;
  };
}

export const slice = createSlice<GlobalState, SliceCaseReducers<GlobalState>>({
  name: 'global',
  initialState: {
    homeDrawer: {
      show: false,
      model: null,
    },
    resourceModal: {
      show: false,
      slug: null,
      unique_id: null,
      isEditing: false,
    },
    articleModal: {
      show: false,
      slug: null,
      unique_id: null,
      isEditing: false,
    },
    tiptapEditorFocus: false,
    dialogOpen: false,
    windowDimensions: {
      width: 0,
      height: 0,
    },
  },
  reducers: {
    setHomeDrawer: (state, action: { payload: IGlobalStateHomeDrawer }) => ({
      ...state,
      homeDrawer: action.payload,
    }),
    closeDrawer: (state) => ({
      ...state,
      homeDrawer: {
        model: null,
        show: false,
      },
    }),
    setResourceModal: (state, action: { payload: IGlobalStateResourceModal }) => ({
      ...state,
      resourceModal: action.payload,
    }),
    closeResourceModal: (state) => ({
      ...state,
      resourceModal: {
        show: false,
        slug: null,
        unique_id: null,
        isEditing: false,
      },
    }),
    setArticleModal: (state, action: { payload: IGlobalStateArticleModal }) => ({
      ...state,
      articleModal: action.payload,
    }),
    closeArticleModal: (state) => ({
      ...state,
      articleModal: {
        show: false,
        slug: null,
        unique_id: null,
        isEditing: false,
      },
    }),
    setTiptapEditorFocus: (state, action: { payload: boolean }) => ({
      ...state,
      tiptapEditorFocus: action.payload,
    }),
    setIsEditing: (state, action: { payload: boolean }) => ({
      ...state,
      resourceModal: {
        ...state.resourceModal,
        isEditing: action.payload,
      },
    }),
    setDialogOpen: (state, action: { payload: boolean }) => ({
      ...state,
      dialogOpen: action.payload,
    }),
    setWindowDimensions: (state, action: { payload: { width: number; height: number } }) => ({
      ...state,
      windowDimensions: action.payload,
    }),
  },
});

export const {
  setHomeDrawer,
  closeDrawer,
  setResourceModal,
  closeResourceModal,
  setArticleModal,
  closeArticleModal,
  setTiptapEditorFocus,
  setIsEditing,
  setDialogOpen,
  setWindowDimensions,
} = slice.actions;

export const homeDrawerSelector = (state): IGlobalStateHomeDrawer => state.global.homeDrawer;
export const resourceModalSelector = (state): IGlobalStateResourceModal =>
  state.global.resourceModal;
export const articleModalSelector = (state): IGlobalStateArticleModal => state.global.articleModal;
export const tiptapEditorFocusSelector = (state): boolean => state.global.tiptapEditorFocus;
export const isEditingSelector = (state): boolean => state.global.resourceModal.isEditing;
export const dialogOpenSelector = (state): boolean => state.global.dialogOpen;
export const windowDimensionsSelector = (state): { width: number; height: number } =>
  state.global.windowDimensions;
export default slice.reducer;
